import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, MoreVertical, Eye, Edit, Trash2, Play, Pause, X, Search, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { recurringInvoicesApi, clientsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatDate } from '@/lib/utils'
import type { RecurringInvoiceStatus } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const statusColors: Record<RecurringInvoiceStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  PAUSED: 'bg-yellow-100 text-yellow-800',
  COMPLETED: 'bg-blue-100 text-blue-800',
  CANCELLED: 'bg-gray-100 text-gray-500',
}

const ALL_STATUSES = 'ALL_STATUSES'
const ALL_CLIENTS = 'ALL_CLIENTS'

export default function RecurringInvoicesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [clientSearchOpen, setClientSearchOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedRecurringId, setSelectedRecurringId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Filter form state (what user is typing/selecting)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClientId, setSelectedClientId] = useState<string>(ALL_CLIENTS)
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES)

  // Applied filters state (what is actually used for filtering)
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
  const [appliedClientId, setAppliedClientId] = useState<string>(ALL_CLIENTS)
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>(ALL_STATUSES)

  // Fetch clients for dropdown
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getAllLegacy(),
  })

  const { data: recurringInvoices = [], isLoading, error } = useQuery({
    queryKey: ['recurring-invoices'],
    queryFn: () => recurringInvoicesApi.getAll({}),
  })

  // Filter recurring invoices based on APPLIED filters (client-side filtering)
  const filteredRecurringInvoices = useMemo(() => {
    return recurringInvoices.filter(recurring => {
      // Search term filter (name or client name)
      if (appliedSearchTerm) {
        const searchLower = appliedSearchTerm.toLowerCase()
        const matchesName = recurring.recurring_name.toLowerCase().includes(searchLower)
        const matchesClient = recurring.client_name.toLowerCase().includes(searchLower)
        if (!matchesName && !matchesClient) return false
      }

      // Client filter
      if (appliedClientId !== ALL_CLIENTS && recurring.client_id !== appliedClientId) {
        return false
      }

      // Status filter
      if (appliedStatusFilter !== ALL_STATUSES && recurring.status !== appliedStatusFilter) {
        return false
      }

      return true
    })
  }, [recurringInvoices, appliedSearchTerm, appliedClientId, appliedStatusFilter])

  // Pagination calculations
  const totalRecords = filteredRecurringInvoices.length
  const totalPages = Math.ceil(totalRecords / pageSize)

  const paginatedRecurringInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredRecurringInvoices.slice(startIndex, endIndex)
  }, [filteredRecurringInvoices, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  // Apply filters when Search is clicked
  const applyFilters = () => {
    setAppliedSearchTerm(searchTerm)
    setAppliedClientId(selectedClientId)
    setAppliedStatusFilter(statusFilter)
    setCurrentPage(1)
  }

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => recurringInvoicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-invoices'] })
      toast.success('Recurring invoice deleted successfully')
      setDeleteDialogOpen(false)
      setSelectedRecurringId(null)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete recurring invoice'
      toast.error(errorMessage)
    },
  })

  // Status update mutation
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: RecurringInvoiceStatus }) =>
      recurringInvoicesApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-invoices'] })
      toast.success('Status updated successfully')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update status'
      toast.error(errorMessage)
    },
  })

  // Get selected client name for display
  const selectedClient = selectedClientId !== ALL_CLIENTS ? clients.find(c => c.client_id === selectedClientId) : null

  // Check if any filters are applied (based on applied filters)
  const hasActiveFilters = appliedSearchTerm || appliedClientId !== ALL_CLIENTS || appliedStatusFilter !== ALL_STATUSES

  const clearAllFilters = () => {
    setSearchTerm('')
    setSelectedClientId(ALL_CLIENTS)
    setStatusFilter(ALL_STATUSES)
    setAppliedSearchTerm('')
    setAppliedClientId(ALL_CLIENTS)
    setAppliedStatusFilter(ALL_STATUSES)
    setCurrentPage(1)
  }

  const handleDelete = (id: string) => {
    setSelectedRecurringId(id)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (selectedRecurringId) {
      deleteMutation.mutate(selectedRecurringId)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{t('recurringInvoices.loadingRecurringInvoices')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">{t('recurringInvoices.failedToLoad')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between -mt-2">
        <div>
          <h1 className="text-2xl font-bold">{t('recurringInvoices.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('recurringInvoices.manageRecurringInvoices')}
          </p>
        </div>
        {canAddEdit('RECURRING_INVOICES') && (
          <Link to="/billing/recurring-invoices/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t('recurringInvoices.createRecurringInvoice')}
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        {/* Search */}
        <div className="flex-1 min-w-[200px] max-w-[300px]">
          <Input
            placeholder={t('recurringInvoices.searchRecurringInvoice')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
        </div>

        {/* Client */}
        <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={clientSearchOpen}
              className="w-[180px] justify-between font-normal"
            >
              {selectedClient ? selectedClient.client_name : t('billing.allClients')}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command>
              <CommandInput placeholder={t('billing.searchClients')} />
              <CommandList>
                <CommandEmpty>{t('billing.noClientFound')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="__all__"
                    onSelect={() => {
                      setSelectedClientId(ALL_CLIENTS)
                      setClientSearchOpen(false)
                    }}
                  >
                    {t('billing.allClients')}
                  </CommandItem>
                  {clients.map((client) => (
                    <CommandItem
                      key={client.client_id}
                      value={client.client_name}
                      onSelect={() => {
                        setSelectedClientId(client.client_id)
                        setClientSearchOpen(false)
                      }}
                    >
                      {client.client_name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Status */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('billing.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('billing.allStatuses')}</SelectItem>
            <SelectItem value="ACTIVE">{t('billing.active')}</SelectItem>
            <SelectItem value="PAUSED">{t('billing.paused')}</SelectItem>
            <SelectItem value="COMPLETED">{t('billing.completed')}</SelectItem>
            <SelectItem value="CANCELLED">{t('billing.cancelled')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Search Button */}
        <Button onClick={applyFilters}>
          <Search className="mr-2 h-4 w-4" />
          {t('common.search')}
        </Button>

        {/* Clear Button */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      {recurringInvoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('recurringInvoices.createFirstRecurringInvoice')}
            </div>
            {canAddEdit('RECURRING_INVOICES') && (
              <Link to="/billing/recurring-invoices/new">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('recurringInvoices.createRecurringInvoice')}
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : filteredRecurringInvoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('recurringInvoices.noMatchingRecurringInvoices')}
            </div>
            <Button variant="outline" onClick={clearAllFilters}>
              <X className="mr-2 h-4 w-4" />
              {t('common.reset')} {t('common.filters')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col h-[calc(100vh-240px)]">
          <Card className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>{t('common.name')}</TableHead>
                    <TableHead>{t('billing.client')}</TableHead>
                    <TableHead>{t('recurringInvoices.frequency')}</TableHead>
                    <TableHead>{t('recurringInvoices.nextInvoice')}</TableHead>
                    <TableHead>{t('recurringInvoices.autoSend')}</TableHead>
                    <TableHead>{t('recurringInvoices.generated')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="w-[100px]">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRecurringInvoices.map((recurring) => (
                    <TableRow key={recurring.recurring_invoice_id}>
                      <TableCell className="font-medium">{recurring.recurring_name}</TableCell>
                      <TableCell>{recurring.client_name}</TableCell>
                      <TableCell>{t(`recurringInvoices.${recurring.frequency_type.toLowerCase().replace('_', '')}`)}</TableCell>
                      <TableCell>{formatDate(recurring.next_invoice_date)}</TableCell>
                      <TableCell>
                        <Badge variant={recurring.auto_send ? "default" : "secondary"}>
                          {recurring.auto_send ? t('common.yes') : t('common.no')}
                        </Badge>
                      </TableCell>
                      <TableCell>{recurring.invoices_generated}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[recurring.status]} variant="secondary">
                          {t(`billing.${recurring.status.toLowerCase()}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/billing/recurring-invoices/${recurring.recurring_invoice_id}`)}>
                              <Eye className="mr-2 h-4 w-4" />
                              {t('recurringInvoices.viewDetails')}
                            </DropdownMenuItem>
                            {canAddEdit('RECURRING_INVOICES') && recurring.status !== 'CANCELLED' && recurring.status !== 'COMPLETED' && (
                              <DropdownMenuItem onClick={() => navigate(`/billing/recurring-invoices/${recurring.recurring_invoice_id}/edit`)}>
                                <Edit className="mr-2 h-4 w-4" />
                                {t('common.edit')}
                              </DropdownMenuItem>
                            )}
                            {canAddEdit('RECURRING_INVOICES') && <DropdownMenuSeparator />}
                            {canAddEdit('RECURRING_INVOICES') && recurring.status === 'ACTIVE' && (
                              <DropdownMenuItem onClick={() => statusMutation.mutate({ id: recurring.recurring_invoice_id, status: 'PAUSED' })}>
                                <Pause className="mr-2 h-4 w-4" />
                                {t('recurringInvoices.pause')}
                              </DropdownMenuItem>
                            )}
                            {canAddEdit('RECURRING_INVOICES') && recurring.status === 'PAUSED' && (
                              <DropdownMenuItem onClick={() => statusMutation.mutate({ id: recurring.recurring_invoice_id, status: 'ACTIVE' })}>
                                <Play className="mr-2 h-4 w-4" />
                                {t('recurringInvoices.resume')}
                              </DropdownMenuItem>
                            )}
                            {canAddEdit('RECURRING_INVOICES') && (recurring.status === 'ACTIVE' || recurring.status === 'PAUSED') && (
                              <>
                                <DropdownMenuItem onClick={() => statusMutation.mutate({ id: recurring.recurring_invoice_id, status: 'CANCELLED' })}>
                                  <X className="mr-2 h-4 w-4" />
                                  {t('common.cancel')}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            {canAddEdit('RECURRING_INVOICES') && (
                              <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(recurring.recurring_invoice_id)}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                {t('common.delete')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination Controls */}
            <div className="flex items-center gap-3 px-4 py-2 border-t">
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[70px] h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                {startRecord}-{endRecord} {t('common.of')} {totalRecords}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('recurringInvoices.deleteRecurringInvoice')}</DialogTitle>
            <DialogDescription>
              {t('recurringInvoices.deleteConfirmation')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

